import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useSendDiciAgreementMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { texts } from 'utils/languages/fr';
import { useDiciUrl } from 'utils/hooks';
import { fundTypes } from 'utils/constants';
import { isStatusOk } from 'utils/helpers';

import TunnelBox from '../common/TunnelBox';
import TunnelStep from '../common/TunnelStep';
import { SubmitButton, PDFViewer, CheckboxLabel } from 'components';
import { Box, Typography, Link } from '@mui/material';

const DICI = props => {
    const { handleNextStep, userStep } = props;

    const [sendDiciAgreement, { isLoading: buttonLoader, isSuccess, reset }] =
        useSendDiciAgreementMutation();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const isFCPR = useMemo(
        () => tunnelSubscription?.investmentFund?.type === fundTypes.FCPR,
        [tunnelSubscription]
    );
    const isSLP = useMemo(
        () => tunnelSubscription?.investmentFund?.type === fundTypes.SLP,
        [tunnelSubscription]
    );
    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const diciUrl = useDiciUrl(tunnelSubscription);

    const [diciAgreementState, setDiciAgreementState] = useState(
        isResubmit ? tunnelSubscription.diciAgreement : false
    );
    const [transactionsAgreementState, setTransactionsAgreementState] = useState(
        isResubmit ? tunnelSubscription?.transactionsAgreement : false
    );

    const [reachedBottom, setReachBottom] = useState(
        isResubmit
            ? isFCPR
                ? diciAgreementState && transactionsAgreementState
                : diciAgreementState
            : false
    );

    const allowSubmit = useMemo(() => {
        if (isFCPR) {
            return !(diciAgreementState && transactionsAgreementState && reachedBottom);
        } else {
            return !(diciAgreementState && reachedBottom);
        }
    }, [isFCPR, reachedBottom, diciAgreementState, transactionsAgreementState]);

    const handleChange = useCallback(setCheckbox => () => setCheckbox(prevState => !prevState), []);

    const handleSubmit = useCallback(() => {
        sendDiciAgreement({
            diciAgreement: diciAgreementState,
            transactionsAgreement: transactionsAgreementState,
        });
    }, [sendDiciAgreement, diciAgreementState, transactionsAgreementState]);

    useEffect(() => {
        if (isSuccess) {
            reset();
            handleNextStep(true);
        }
    }, [isSuccess, handleNextStep, reset]);

    return (
        <TunnelStep title={texts.DICI} tunnelStepWidth={1100} maxWidth={1100}>
            <Box textAlign="center" p={1}>
                <Typography gutterBottom>
                    {isSLP ? texts.PARCOURIR_DICI_SLP : texts.PARCOURIR_DICI}
                </Typography>
            </Box>
            <TunnelBox
                sx={theme => ({ padding: '10px 0', backgroundColor: theme.palette.grey['A700'] })}
            >
                <PDFViewer url={diciUrl} setReachBottom={setReachBottom} />
            </TunnelBox>
            <Box textAlign="center" p={1}>
                <Link
                    href={diciUrl}
                    download
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                        color: 'common.black',
                        '&:hover': { color: 'primary.main' },
                    }}
                >
                    {texts.TELECHARGER_DICI}
                </Link>
            </Box>
            <CheckboxLabel
                onChange={handleChange(setDiciAgreementState)}
                value={diciAgreementState}
                checked={diciAgreementState}
                label={texts.COMPRIS_DICI}
            />
            {isFCPR && (
                <CheckboxLabel
                    onChange={handleChange(setTransactionsAgreementState)}
                    value={transactionsAgreementState}
                    checked={transactionsAgreementState}
                    label={texts.RECONNAIS_AVOIR}
                />
            )}
            <SubmitButton
                text={isResubmit ? texts.SUIVANT : texts.VALIDER}
                disabled={allowSubmit || buttonLoader}
                onClick={hasStatusOk ? handleNextStep : handleSubmit}
                loading={buttonLoader}
            />
        </TunnelStep>
    );
};

export default DICI;
